import { Paperclip, Link as LinkIcon, ExternalLink, FileText, Download, X as XIcon } from "lucide-react";

/**
 * Icon for a single attachment (file/PDF or link).
 */
function AttachmentIcon({ type, mime }) {
  if (type === "link") return <LinkIcon className="w-4 h-4 text-[#0D9488]" />;
  if (mime === "application/pdf" || (mime || "").startsWith("application/")) {
    return <FileText className="w-4 h-4 text-[#0D9488]" />;
  }
  return <Paperclip className="w-4 h-4 text-[#0D9488]" />;
}

/** Compact chip for the composer (removable). */
export function AttachmentChip({ att, onRemove }) {
  const isLink = att.type === "link";
  const label = isLink ? (att.title || att.url) : att.filename;
  const sub = isLink ? att.url : `${(att.mime || "file")}${att.size ? " · " + humanBytes(att.size) : ""}`;
  return (
    <div
      className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 px-2.5 h-9 max-w-full"
      data-testid={`composer-attachment-chip-${att.type}`}
    >
      <AttachmentIcon type={att.type} mime={att.mime} />
      <div className="flex flex-col leading-tight min-w-0">
        <span className="text-xs text-slate-900 truncate max-w-[220px]">{label}</span>
        <span className="font-mono-num text-[9px] text-slate-500 uppercase tracking-widest truncate max-w-[220px]">{sub}</span>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="ml-1 text-slate-500 hover:text-red-400"
        aria-label="Remove attachment"
        data-testid="composer-attachment-remove"
      >
        <XIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

/** Rendered on the topic detail view. */
export function TopicAttachments({ attachments = [] }) {
  if (!attachments.length) return null;
  return (
    <div className="mt-4 space-y-2" data-testid="topic-attachments">
      {attachments.map((att, i) => (
        <AttachmentCard key={i} att={att} />
      ))}
    </div>
  );
}

function AttachmentCard({ att }) {
  const isLink = att.type === "link";
  const href = isLink ? att.url : att.data_url;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      download={!isLink ? att.filename : undefined}
      className="flex items-center gap-3 border border-slate-200 hover:border-[#0D9488] bg-slate-50 hover:bg-slate-50 px-3 py-2.5 transition-colors group"
      data-testid={`topic-attachment-${att.type}`}
    >
      <div className="w-9 h-9 flex items-center justify-center border border-slate-200 bg-black shrink-0">
        <AttachmentIcon type={att.type} mime={att.mime} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-slate-900 group-hover:text-[#0D9488] transition-colors truncate">
          {isLink ? (att.title || att.url) : att.filename}
        </div>
        <div className="font-mono-num text-[10px] text-slate-500 uppercase tracking-widest truncate">
          {isLink
            ? att.url
            : `${att.mime || "file"}${att.size ? " · " + humanBytes(att.size) : ""}`}
        </div>
      </div>
      {isLink ? <ExternalLink className="w-4 h-4 text-slate-500" /> : <Download className="w-4 h-4 text-slate-500" />}
    </a>
  );
}

export function humanBytes(n) {
  if (!n && n !== 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}
